import { Dispatch, SetStateAction, useState } from 'react'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import { KTIcon } from '../../../../../../../_metronic/helpers'
import { formatSum } from '../../../../../../../utils'
import { toggleActiveRequisite, releaseReservationRequisite } from '../../../../core/_requests'
import { requisiteToggleErrorText } from '../../core/toggleErrors'
import { postEnableReservation } from '../../../../api/postEnableReservation'
import { IRequisite } from '../../../../../../../types'
import { IsGranted, useAuth } from '../../../../../../modules/auth'
import { REQUISITE_WRITE_ROLES } from '../../../Requisites/core/access'

const AppSwal = withReactContent(Swal)

const FastTrackRequisiteItem = (props: {
  requisite: IRequisite
  requisiteItemCallback: () => void
  setRequisiteList: Dispatch<SetStateAction<IRequisite[]>>
}) => {
  let activeBadge
  let reserveBadge

  const { currentUser } = useAuth()
  // Enable/disable and reservation change the requisite; readers see the state only.
  const canWrite = IsGranted(REQUISITE_WRITE_ROLES, currentUser)
  const [requisite, setRequisite] = useState(props.requisite)
  const [loading, setLoading] = useState<{
    type: 'enabling' | 'disableReservation'
    isLoading: boolean
  }>({
    type: 'enabling',
    isLoading: false,
  })

  const toggleRequisite = async () => {
    setLoading({
      type: 'enabling',
      isLoading: true,
    })

    try {
      const response = await toggleActiveRequisite(requisite.id.toString())

      if (response.data?.success && response.data.requisite) {
        props.requisiteItemCallback()
      }
    } catch (error) {
      Swal.fire({ title: 'Ошибка', text: requisiteToggleErrorText(error), icon: 'error' })
    } finally {
      setLoading({
        type: 'enabling',
        isLoading: false,
      })
    }
  }

  const releaseRequisite = async () => {
    if (!requisite.isReserved || (await askStaffAboutAction()).isConfirmed) {
      let response = await releaseReservationRequisite(requisite.id.toString())

      if (!response.data) {
        return false
      }

      if (response.data.requisite && response.data.success) {
        setRequisite((prev) =>
          prev.id === response?.data?.requisite?.id ? response?.data?.requisite : prev
        )
      }
    }
  }

  const enableReservation = async () => {
    const response = await postEnableReservation(`${requisite.id} `)

    if (!response.data) {
      return false
    }

    if (response.data.success && response.data.requisite) {
      const { currentPaymentLimitByAmount, currentPaymentLimitByTicketCount, ...rest } =
        response.data.requisite

      props.requisiteItemCallback()
    }
  }

  const askStaffAboutAction = () => {
    return AppSwal.fire({
      title: (
        <p>При снятии с резервирования, все связанные активные тикеты отменяются. Продолжить?</p>
      ),
      cancelButtonText: 'Нет',
      confirmButtonText: 'Да',
      showCancelButton: true,
    })
  }

  // Функция для отображения информации о клиентах fast track
  const renderFastTrackClients = () => {
    const connectedClients = requisite.connectedClientsCount || 0
    const maxClients = requisite.fastTrackMaxClients || 0
    const availableClients = maxClients - connectedClients

    return (
      <div>
        <div>Подключено: {connectedClients}</div>
        <div>
          Доступно: <span className={availableClients > 0 ? 'text-success' : 'text-danger'}>
            {availableClients}
          </span>
        </div>
      </div>
    )
  }

  if (requisite.enabled) {
    activeBadge = <span className='badge badge-light-success'>Включён</span>
  } else {
    activeBadge = <span className='badge badge-light-danger'>Отключён</span>
  }

  if (requisite.isReserved) {
    reserveBadge = (
      <span
        onClick={releaseRequisite}
        className='badge badge-light-info cursor-pointer'
        title='Снять с резерва'
      >
        Зарезервирован
      </span>
    )
  } else {
    reserveBadge = <span className='badge badge-light-success'>Свободен</span>
  }

  return (
    <tr>
      <td className='fw-bold'>{requisite.id}</td>
      <td className='text-break'>{requisite.name}</td>
      <td>
        <span className='badge'>{requisite.requisiteType}</span>
        <span className='separator' />
        {requisite.paymentMethods.map((pm) => (
          <span className='badge'>{pm.type}</span>
        ))}
      </td>
      <td>{requisite.cashBox.title}</td>
      <td>
        <div>Номер карты: {requisite.cardNumber || '—'}</div>
        <div>Номер телефона: {requisite.phone || '-'}</div>
        <div>Держатель карты: {requisite.cardholderName}</div>
      </td>
      <td>{requisite.accountNumber || '-'}</td>
      <td>{formatSum(requisite.balance)}</td>
      <td align='center'>
        <span
          className={`badge ${
            requisite.currentPaymentLimitByAmount === requisite.paymentLimitByAmount
              ? 'badge-light-danger'
              : ''
          }`}
        >
          {`${new Intl.NumberFormat()
            .format(requisite.currentPaymentLimitByAmount)
            .replaceAll(',', ' ')} / ${new Intl.NumberFormat()
            .format(requisite.paymentLimitByAmount)
            .replaceAll(',', ' ')}`}
        </span>
      </td>
      <td align='center'>
        <span
          className={`badge ${
            requisite.currentPaymentLimitByTicketCount === requisite.paymentLimitByTicketCount
              ? 'badge-light-danger'
              : ''
          }`}
        >
          {`${new Intl.NumberFormat()
            .format(requisite.currentPaymentLimitByTicketCount)
            .replaceAll(',', ' ')} / ${new Intl.NumberFormat()
            .format(requisite.paymentLimitByTicketCount)
            .replaceAll(',', ' ')}`}
        </span>
      </td>
      <td align='center'>
        <span className={`badge`}>{`${requisite?.minDeposit}-${requisite?.maxDeposit}`}</span>
      </td>
      <td align='center'>
        <div className='d-flex flex-column'>
          <div className='pb-1'>
            {canWrite ? (
              <button
                onClick={toggleRequisite}
                title={requisite.enabled ? 'Отключить' : 'Включить'}
                className='btn p-0'
              >
                {loading.type === 'enabling' && loading.isLoading ? (
                  <span className='badge badge-light-warning'>Идет запрос...</span>
                ) : (
                  activeBadge
                )}
              </button>
            ) : (
              activeBadge
            )}
          </div>
          <div className='pb-1'>
            {canWrite ? (
              <button
                onClick={enableReservation}
                title={requisite.disableReservation ? 'Недоступно' : 'Доступно'}
                className='btn p-0'
              >
                {requisite.disableReservation ? (
                  <span className='badge'>Недоступно</span>
                ) : (
                  <span className='badge'>Доступно</span>
                )}
              </button>
            ) : (
              <span className='badge'>{requisite.disableReservation ? 'Недоступно' : 'Доступно'}</span>
            )}
          </div>
          <div>{reserveBadge}</div>
        </div>
      </td>
      <td className='text-nowrap'>{renderFastTrackClients()}</td>
      <td className='text-end'>
        <div className=''>
          <a
            href={'/settings/requisite/edit/' + requisite.id}
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm mb-1'
            title={canWrite ? 'Редактировать' : 'Просмотр'}
          >
            <KTIcon iconName={canWrite ? 'pencil' : 'eye'} className='fs-3' />
          </a>
          <span className='separator' />
          <a
            href={'/settings/requisite/clients/' + requisite.id}
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm mt-1'
          >
            <KTIcon iconName='profile-user' className='fs-3' />
          </a>
        </div>
      </td>
    </tr>
  )
}

export default FastTrackRequisiteItem 